import React from "react";
import { ArrowRight } from "lucide-react";
import ProjectDiscoveryAssistant from "./ProjectDiscoveryAssistant";
import { useLanguage } from "../context/LanguageContext";

const resolveSectionLanguage = (language) => {
  const normalized = String(language || "").toLowerCase();
  const base = normalized.split("-")[0];
  return sectionCopy[base] ? base : "en";
};

const sectionCopy = {
  en: {
    eyebrow: "Discovery",
    title: "Start your project assessment",
    description:
      "Through this discovery assistant, we want to classify your requirements and understand the best way we can support your project.",
  },
  es: {
    eyebrow: "Discovery",
    title: "Iniciá la evaluación de tu proyecto",
    description:
      "A través de este asistente de discovery queremos clasificar tus requerimientos y entender cuál es la mejor forma en la que podemos asistirte.",
  },
  pt: {
    eyebrow: "Discovery",
    title: "Inicie a avaliação do seu projeto",
    description:
      "Por meio deste assistente de discovery, queremos classificar seus requisitos e entender qual é a melhor forma de apoiar seu projeto.",
  },
  fr: {
    eyebrow: "Discovery",
    title: "Démarrez l’évaluation de votre projet",
    description:
      "Grâce à cet assistant de discovery, nous voulons classer vos besoins et comprendre la meilleure manière de vous accompagner.",
  },
  de: {
    eyebrow: "Discovery",
    title: "Starten Sie die Bewertung Ihres Projekts",
    description:
      "Mit diesem Discovery-Assistenten möchten wir Ihre Anforderungen einordnen und verstehen, wie wir Ihr Projekt am besten unterstützen können.",
  },
  it: {
    eyebrow: "Discovery",
    title: "Avvia la valutazione del tuo progetto",
    description:
      "Attraverso questo assistente di discovery vogliamo classificare le tue esigenze e capire il modo migliore per supportare il tuo progetto.",
  },
  zh: {
    eyebrow: "Discovery",
    title: "开始您的项目评估",
    description:
      "通过此 discovery 助手，我们希望梳理您的需求，并了解我们如何以最佳方式支持您的项目。",
  },
  ja: {
    eyebrow: "Discovery",
    title: "プロジェクト評価を開始する",
    description:
      "この discovery アシスタントを通じて、要件を整理し、最適な形でプロジェクトを支援する方法を把握します。",
  },
  ko: {
    eyebrow: "Discovery",
    title: "프로젝트 평가 시작",
    description:
      "이 discovery 어시스턴트를 통해 요구사항을 분류하고, 프로젝트를 가장 효과적으로 지원할 방법을 파악하고자 합니다.",
  },
  ar: {
    eyebrow: "Discovery",
    title: "ابدأ تقييم مشروعك",
    description:
      "من خلال مساعد الاكتشاف هذا، نهدف إلى تصنيف متطلباتكم وفهم أفضل طريقة يمكننا من خلالها دعم مشروعكم.",
  },
  hi: {
    eyebrow: "Discovery",
    title: "अपने प्रोजेक्ट का मूल्यांकन शुरू करें",
    description:
      "इस discovery सहायक के माध्यम से हम आपकी आवश्यकताओं को वर्गीकृत करना और आपके प्रोजेक्ट में सहायता का सर्वोत्तम तरीका समझना चाहते हैं।",
  },
  ru: {
    eyebrow: "Discovery",
    title: "Начните оценку вашего проекта",
    description:
      "С помощью этого discovery-ассистента мы хотим классифицировать ваши требования и понять, как лучше всего поддержать ваш проект.",
  },
  tr: {
    eyebrow: "Discovery",
    title: "Proje değerlendirmenizi başlatın",
    description:
      "Bu discovery asistanı aracılığıyla ihtiyaçlarınızı sınıflandırmak ve projenizi en iyi nasıl destekleyebileceğimizi anlamak istiyoruz.",
  },
  nl: {
    eyebrow: "Discovery",
    title: "Start de evaluatie van uw project",
    description:
      "Via deze discovery-assistent willen we uw behoeften classificeren en begrijpen hoe we uw project het beste kunnen ondersteunen.",
  },
  pl: {
    eyebrow: "Discovery",
    title: "Rozpocznij ocenę swojego projektu",
    description:
      "Za pośrednictwem tego asystenta discovery chcemy sklasyfikować Twoje wymagania i zrozumieć, jak najlepiej możemy wesprzeć Twój projekt.",
  },
  id: {
    eyebrow: "Discovery",
    title: "Mulai penilaian proyek Anda",
    description:
      "Melalui asisten discovery ini, kami ingin mengklasifikasikan kebutuhan Anda dan memahami cara terbaik untuk mendukung proyek Anda.",
  },
};

const ContactSection = () => {
  const { language } = useLanguage();
  const uiLanguage = resolveSectionLanguage(language);
  const copy = sectionCopy[uiLanguage];

  return (
    <section id="contact" className="py-32 lg:py-40 bg-black relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-px h-32 bg-gradient-to-b from-cyan-400/20 to-transparent" />
      <div className="absolute bottom-0 right-1/3 w-px h-48 bg-gradient-to-t from-cyan-400/20 to-transparent" />

      <div className="absolute bottom-0 right-0 text-[20vw] font-extralight text-zinc-950 whitespace-nowrap select-none pointer-events-none translate-x-1/4 translate-y-1/4">
        DISCOVERY
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-px bg-gradient-to-r from-cyan-400 to-transparent" />
              <span className="text-xs tracking-[0.3em] text-cyan-400 uppercase font-medium">{copy.eyebrow}</span>
            </div>

            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-extralight text-white mb-8 leading-tight">
              {copy.title}
            </h2>
            <p className="text-lg text-zinc-500 font-light leading-relaxed mb-12">
              {copy.description}
            </p>

            <div className="space-y-6">
              <div className="group flex items-center gap-4">
                <div className="w-12 h-12 flex items-center justify-center border border-zinc-800 group-hover:border-cyan-500/50 transition-colors">
                  <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-cyan-400 transition-colors" />
                </div>
                <span className="text-zinc-400 group-hover:text-white transition-colors">interware.dev</span>
              </div>
            </div>
          </div>

          <div>
            <ProjectDiscoveryAssistant />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
